import React from 'react';
import { Box, Typography } from '@mui/material';
import LottoPosTile from '../../LottoPosTile';
import SummaryTile from '../../SummaryTile';
import { ManualAmountInput } from '../components/ManualAmountInput';
import { TileCheckbox } from '../components/TileCheckbox';
import type { ManualOverallCollectionsTileConfig, TileConfig } from '../types';
import type { ManualDayEntryFormValues } from '../../manualDayEntry';
import { formatCurrency } from 'utils';
import ReviewIcon from 'assets/images/riviewdicon.svg';

export function OverallTile({
  config,
  isLoading,
  isAllShift,
  isLoadingManualDayEntry,
  isSavingManualDayEntry,
  manualFormValues,
  onManualFieldChange,
  localVerified,
  localAck,
  onRequestDialog,
}: {
  config: TileConfig;
  isLoading: boolean;
  isAllShift: boolean;
  isLoadingManualDayEntry: boolean;
  isSavingManualDayEntry: boolean;
  manualFormValues: ManualDayEntryFormValues;
  onManualFieldChange: (field: keyof ManualDayEntryFormValues, nextValue: string) => void;
  localVerified: boolean;
  localAck: boolean;
  onRequestDialog: (args: { mode: 'verify' | 'ack'; nextVerified: boolean; nextAck: boolean }) => void;
}) {
  if (!config.visible) return null;

  if (config.variant === 'manual_overall_collections') {
    const typed = config as ManualOverallCollectionsTileConfig;
    const disabled = isLoadingManualDayEntry || isSavingManualDayEntry;
    const overallInputWidth = 74;

    return (
      <SummaryTile
        title="OVERALL"
        cardClassName={typed.cardClassName}
        value={
          typed.useBreakdownOverall ? (
            <Box className="overall-manual-breakdown">
              <Box className="overall-manual-breakdown__row">
                <Box className="overall-manual-breakdown__label-group">
                  <Box component="span" className="overall-manual-breakdown__label-text">
                    Cash Collection
                  </Box>
                </Box>
                <ManualAmountInput
                  ariaLabel="Cash Collection"
                  value={manualFormValues.cashCollectionAmount}
                  onChange={(next) => onManualFieldChange('cashCollectionAmount', next)}
                  disabled={disabled}
                  width={overallInputWidth}
                  isOverallCompact={true}
                />
              </Box>
              <Box className="overall-manual-breakdown__row">
                <Box className="overall-manual-breakdown__label-group">
                  <Box component="span" className="overall-manual-breakdown__label-prefix">
                    add
                  </Box>
                  <Box component="span" className="overall-manual-breakdown__label-text">
                    Credit Card Collection
                  </Box>
                </Box>
                <ManualAmountInput
                  ariaLabel="Credit Card Collection"
                  value={manualFormValues.creditCardCollectionAmount}
                  onChange={(next) => onManualFieldChange('creditCardCollectionAmount', next)}
                  disabled={disabled}
                  width={overallInputWidth}
                  isOverallCompact={true}
                />
              </Box>
              <Box className="overall-manual-breakdown__divider" />
              <Box className="overall-manual-breakdown__math-row">
                <Box className="overall-manual-breakdown__label-group">
                  <Box component="span" className="overall-manual-breakdown__label-prefix">
                    add
                  </Box>
                  <Box component="span" className="overall-manual-breakdown__label-text">
                    Payout
                  </Box>
                </Box>
                <Box component="span" className="overall-manual-breakdown__math-value">
                  {formatCurrency(typed.effectivePayouts)}
                </Box>
              </Box>
              <Box className="overall-manual-breakdown__math-row">
                <Box className="overall-manual-breakdown__label-group">
                  <Box component="span" className="overall-manual-breakdown__label-prefix">
                    less
                  </Box>
                  <Box component="span" className="overall-manual-breakdown__label-text">
                    Scratcher Sales
                  </Box>
                </Box>
                <Box component="span" className="overall-manual-breakdown__math-value">
                  {formatCurrency(typed.effectiveScratcherSales)}
                </Box>
              </Box>
              <Box className="overall-manual-breakdown__math-row">
                <Box className="overall-manual-breakdown__label-group">
                  <Box component="span" className="overall-manual-breakdown__label-prefix">
                    less
                  </Box>
                  <Box component="span" className="overall-manual-breakdown__label-text">
                    Lotto Sales
                  </Box>
                </Box>
                <Box component="span" className="overall-manual-breakdown__math-value">
                  {formatCurrency(typed.effectiveLottoSales)}
                </Box>
              </Box>
              <Box className="overall-manual-breakdown__divider overall-manual-breakdown__divider--balance" />
              <Box className="overall-manual-breakdown__balance-row">
                <Box className="overall-manual-breakdown__balance-left">
                  <Box component="span" className="overall-manual-breakdown__balance-label">
                    Balance
                  </Box>
                  <Box className={typed.balanceBadgeClassName}>{typed.balanceLabel}</Box>
                </Box>
                {typed.isMatched ? (
                  <img
                    src={ReviewIcon}
                    alt="Matched"
                    className="review-icon"
                    style={{ width: '28px', height: '28px' }}
                  />
                ) : (
                  <Box component="span" className="overall-manual-breakdown__balance-value" sx={{ color: typed.balanceColor }}>
                    {formatCurrency(typed.totalVariance)}
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25, width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Cash Collection</Typography>
                <ManualAmountInput
                  ariaLabel="Cash Collection"
                  value={manualFormValues.cashCollectionAmount}
                  onChange={(next) => onManualFieldChange('cashCollectionAmount', next)}
                  disabled={disabled}
                  width={150}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Credit Card Collection</Typography>
                <ManualAmountInput
                  ariaLabel="Credit Card Collection"
                  value={manualFormValues.creditCardCollectionAmount}
                  onChange={(next) => onManualFieldChange('creditCardCollectionAmount', next)}
                  disabled={disabled}
                  width={150}
                />
              </Box>
            </Box>
          )
        }
        subtext={
          !typed.useBreakdownOverall ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Box className={typed.balanceBadgeClassName}>{typed.balanceLabel}</Box>
              {typed.isMatched ? (
                <img
                  src={ReviewIcon}
                  alt="Matched"
                  className="review-icon"
                  style={{ width: '32px', height: '32px' }}
                />
              ) : (
                <Typography sx={{ fontSize: '22px', fontWeight: 700, color: typed.balanceColor }}>
                  {formatCurrency(typed.totalVariance)}
                </Typography>
              )}
            </Box>
          ) : undefined
        }
        footer={typed.footer}
        isLoading={isLoading || isLoadingManualDayEntry}
      />
    );
  }

  const shouldShowAction = config.badge !== 'MATCHED';
  const action =
    config.actionLabel && shouldShowAction ? (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <TileCheckbox
            isAllShift={isAllShift}
            label={config.actionLabel}
            localVerified={localVerified}
            localAck={localAck}
            onRequestDialog={onRequestDialog}
          />
        </Box>
      </Box>
    ) : null;

  return (
    <LottoPosTile
      title={config.title}
      badge={config.badge}
      value={config.value}
      footer={config.footer}
      isLoading={isLoading}
      cardClassName={config.cardClassName}
    >
      {action}
    </LottoPosTile>
  );
}
